// Currency column for Target Value that additionally pins unset (blank) values to the bottom in BOTH sort
// directions. The design-system CurrencyColumn renders the value (a dash for a missing one) and formats the
// currency, but its column definition does not carry `sortUndefined`, so a blank Target Value sorts on its
// comparator value (0) — the bottom when descending but the top when ascending. We reuse the DS
// CurrencyColumn's column definition verbatim and re-attach `sortUndefined` so rows whose sorted value is
// `undefined` (a blank / $0 target) sort last regardless of direction, matching the Win Probability column.
import React from 'react';
import type {ColumnDef} from '@tanstack/react-table';
import type {IntlShape} from 'react-intl';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import CurrencyColumn from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/CurrencyColumn';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import type {CurrencyColumnType} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/CurrencyColumn';

export interface TargetValueColumnType extends CurrencyColumnType {
	// Placement for rows whose sorted value (accessor) is `undefined` — passed straight to the tanstack column
	// so unset rows can be pinned last regardless of sort direction (the DS CurrencyColumn drops this).
	sortUndefined?: 'first' | 'last';
}

// Config-only component: the DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TargetValueColumn = (_: TargetValueColumnType): null => null;

// Returns ColumnDef<any> to match the DS CurrencyColumn.toColumnDef it reuses (its accessorKey is typed
// loosely, so a narrower row-typed ColumnDef would not accept the spread).
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors the DS CurrencyColumn.toColumnDef return type
TargetValueColumn.toColumnDef = (child: React.ReactElement<TargetValueColumnType>, intl: IntlShape): ColumnDef<any> => ({
	...CurrencyColumn.toColumnDef(child, intl),
	sortUndefined: child.props.sortUndefined,
});

export default TargetValueColumn;
